// src/components/common/LeftSidebar.js
//
// Shared shell for the page-level left sidebars (Dashboard, Data, Agent,
// Settings). Owns the rail chrome the pages used to copy-paste — the right
// border, the sidebar background, and the collapsed 56px icon rail — plus
// drag-to-resize along the whole right edge. Collapsing is the drag gesture
// alone (there is no collapse button); the collapsed rail keeps an expand
// arrow, since a bare 56px strip gives no hint that it can be dragged back
// out. Page content is passed as children; `collapsedActions` adds extra
// icon buttons to the collapsed rail (e.g. the Agent page's "new chat").
//
// Width is a single pixel value persisted in localStorage and shared by every
// page. Pixels rather than a viewport fraction: fonts don't scale with the
// viewport, so "wide enough to read my table names" is a pixel quantity — a
// stored percentage would fit the names on one monitor and truncate them on
// another. The 45vw cap below keeps a width chosen on a large screen from
// eating a small one, without touching the stored value.
//
// Collapse is component state (or the parent's, when `collapsed` /
// `onCollapsedChange` are passed — the Agent page persists it and shows an
// expand affordance in its top bar). A drag released below COLLAPSE_THRESHOLD
// is a collapse gesture, not a resize: the stored width is untouched, so
// re-expanding restores the width the user actually chose. The threshold sits
// below MIN_WIDTH deliberately — the band between them pins at MIN_WIDTH, so
// a drag has to pull decisively past the minimum to snap closed.
//
// During a drag the width is written straight to the rail's DOM node and the
// neighbouring content follows via flexbox — no React render per pointer
// move. The preview uses the same formula as the release commit, so the rail
// never lands somewhere the committed state disagrees with. State and
// storage are written once, on release.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

const STORAGE_KEY = 'decode.left_sidebar_width';
const DEFAULT_WIDTH = 250;
const MIN_WIDTH = 180;
const MAX_WIDTH = 440;
const COLLAPSE_THRESHOLD = 130;
const COLLAPSED_WIDTH = 56;

const clampWidth = (px) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(px)));

function readStoredWidth() {
  try {
    const stored = Number(window.localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(stored) && stored > 0) return clampWidth(stored);
  } catch {
    // Storage unavailable — fall through to the default.
  }
  return DEFAULT_WIDTH;
}

function storeWidth(px) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(px));
  } catch {
    // Storage unavailable — the width just won't persist.
  }
}

function LeftSidebar({
  children,
  collapsedActions = null,
  collapsed: collapsedProp,
  onCollapsedChange,
}) {
  const [width, setWidth] = useState(readStoredWidth);
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const controlled = collapsedProp !== undefined;
  const collapsed = controlled ? collapsedProp : internalCollapsed;

  const railRef = useRef(null);
  // { pointerId, startX, startWidth } while a drag is live, else null.
  const dragRef = useRef(null);

  const setCollapsed = useCallback((next) => {
    if (!controlled) setInternalCollapsed(next);
    if (onCollapsedChange) onCollapsedChange(next);
  }, [controlled, onCollapsedChange]);

  const releaseBodyStyles = () => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  const beginDrag = (event) => {
    if (event.button !== 0) return;
    const rail = railRef.current;
    if (!rail) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      // Measure rather than trust state: the 45vw cap can render the rail
      // narrower than the stored width, and the drag must track from what
      // the user actually sees.
      startWidth: rail.getBoundingClientRect().width,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const dragWidthAt = (event) => (
    dragRef.current.startWidth + (event.clientX - dragRef.current.startX)
  );

  const moveDrag = (event) => {
    if (!dragRef.current || event.pointerId !== dragRef.current.pointerId) return;
    const rail = railRef.current;
    if (!rail) return;
    const raw = dragWidthAt(event);
    // Live preview: below the threshold the rail snaps to its collapsed
    // size; pulling back out restores the clamped width.
    rail.style.width = `${raw < COLLAPSE_THRESHOLD ? COLLAPSED_WIDTH : clampWidth(raw)}px`;
  };

  const endDrag = (event) => {
    if (!dragRef.current || event.pointerId !== dragRef.current.pointerId) return;
    const raw = dragWidthAt(event);
    dragRef.current = null;
    releaseBodyStyles();
    if (raw < COLLAPSE_THRESHOLD) {
      // A collapse gesture, not a resize — the stored width survives.
      setCollapsed(true);
    } else {
      const next = clampWidth(raw);
      if (collapsed) setCollapsed(false);
      setWidth(next);
      storeWidth(next);
    }
  };

  // Pointer capture lost to something other than a release (e.g. an alert):
  // abandon the drag and put the rail back where the state says it belongs.
  const cancelDrag = (event) => {
    if (!dragRef.current || event.pointerId !== dragRef.current.pointerId) return;
    dragRef.current = null;
    releaseBodyStyles();
    const rail = railRef.current;
    if (rail) rail.style.width = `${collapsed ? COLLAPSED_WIDTH : width}px`;
  };

  const resetWidth = () => {
    if (collapsed) setCollapsed(false);
    setWidth(DEFAULT_WIDTH);
    storeWidth(DEFAULT_WIDTH);
  };

  // If the component unmounts mid-drag, the body cursor/user-select must not
  // leak into the rest of the app.
  useEffect(() => () => {
    if (dragRef.current) {
      dragRef.current = null;
      releaseBodyStyles();
    }
  }, []);

  return (
    <Box
      ref={railRef}
      style={{ width: collapsed ? COLLAPSED_WIDTH : width }}
      sx={{
        position: 'relative',
        height: '100%',
        maxWidth: '45vw',
        flexShrink: 0,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        borderRight: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: (theme) => theme.palette.custom?.sidebarBg || theme.palette.background.paper,
      }}
    >
      {collapsed ? (
        <Box sx={{ pt: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton size="small" onClick={() => setCollapsed(false)}>
              <KeyboardDoubleArrowRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {collapsedActions}
        </Box>
      ) : (
        /* The overflow guard lives here rather than on the rail so the drag
           handle can straddle the right border without being clipped. */
        <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {children}
        </Box>
      )}
      {/* Drag handle: a slim strip straddling the right border (3px inside,
          3px outside), so the grab zone barely overlaps the content's own
          scrollbars on either side. */}
      <Box
        onPointerDown={beginDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={cancelDrag}
        onDoubleClick={resetWidth}
        sx={{
          position: 'absolute',
          top: 0,
          right: -3,
          bottom: 0,
          width: 6,
          zIndex: 10,
          cursor: 'col-resize',
          touchAction: 'none',
          '&:hover::after, &:active::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 1,
            bottom: 0,
            width: '3px',
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.45),
          },
        }}
      />
    </Box>
  );
}

export default LeftSidebar;
